import { useEffect, useState } from "react";

type Props = {
  onOpenConsult: () => void;
};

const links = [
  { label: "Concern", href: "#concern" },
  { label: "Results", href: "#results" },
  { label: "Practice", href: "#practice" },
  { label: "First Visit", href: "#first-visit" },
  { label: "Treatments", href: "#treatments" },
  { label: "Pricing", href: "#pricing" },
  { label: "Contact", href: "#contact" },
];

export default function Nav({ onOpenConsult }: Props) {
  const [solid, setSolid] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const smoothScrollTo = (href: string) => {
    const id = href.replace(/^#/, "");
    const el = id === "top" ? document.body : document.getElementById(id);
    if (!el) return;
    const headerOffset = 72;
    const targetY =
      id === "top"
        ? 0
        : el.getBoundingClientRect().top + window.scrollY - headerOffset;
    const startY = window.scrollY;
    const distance = targetY - startY;
    if (Math.abs(distance) < 2) return;
    const duration = Math.min(1400, Math.max(700, Math.abs(distance) * 0.55));
    const startTime = performance.now();
    // easeInOutCubic — calm, natural glide
    const ease = (t: number) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const step = (now: number) => {
      const p = Math.min(1, (now - startTime) / duration);
      window.scrollTo(0, startY + distance * ease(p));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!href.startsWith("#")) return;
    e.preventDefault();
    setMobileOpen(false);
    smoothScrollTo(href);
    if (history.replaceState) history.replaceState(null, "", href);
  };

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-[60] transition-all duration-300 ${
        solid ? "bg-white/95 backdrop-blur border-b border-gray-100" : "bg-transparent"
      }`}
    >
      <div className="w-full px-6 md:px-10 h-16 md:h-[72px]">
        <div className="max-w-ds-wide mx-auto h-full flex items-center justify-between">
        <a href="#top" onClick={(e) => handleNavClick(e, "#top")} className="flex items-center gap-2.5 cursor-pointer group">
          <span className="w-8 h-8 flex items-center justify-center text-ink group-hover:text-brand transition-colors">
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none" aria-hidden="true">
              <path d="M16 5c-3 0-5 1-7 1-2 0-3.2 1.3-3.2 3.3 0 3.1 1 6.9 2.3 10.3 1 2.5 1.9 4.7 2.8 6 .6.9 1.2 1.4 1.9 1.4 1 0 1.4-.9 2-2.8.5-1.7 1-2.5 1.2-2.9.3-.3.6-.5 1-.5s.7.2 1 .5c.2.4.7 1.2 1.2 2.9.6 1.9 1 2.8 2 2.8.7 0 1.3-.5 1.9-1.4.9-1.3 1.8-3.5 2.8-6 1.3-3.4 2.3-7.2 2.3-10.3C28.2 7.3 27 6 25 6c-2 0-4-1-7-1Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
            </svg>
          </span>
          <span className="text-[15px] tracking-tight leading-none">
            <span className="font-semibold text-ink">Aline</span>
            <span className="font-light text-ink-muted ml-1">dental</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={(e) => handleNavClick(e, l.href)}
              className="text-ds-meta text-ink hover:text-brand transition whitespace-nowrap cursor-pointer"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={onOpenConsult}
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand text-white text-ds-control font-medium hover:bg-[#027ea3] transition whitespace-nowrap cursor-pointer"
          >
            Book a Consultation
            <i className="ri-arrow-right-line text-sm" />
          </button>
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer"
            aria-label="Menu"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
          </button>
        </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4">
          <div className="max-w-ds-wide mx-auto flex flex-col gap-3">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={(e) => handleNavClick(e, l.href)}
              className="text-ds-control py-2 text-ink cursor-pointer"
            >
              {l.label}
            </a>
          ))}
          <button
            onClick={() => {
              setMobileOpen(false);
              onOpenConsult();
            }}
            className="mt-2 py-3 rounded-md bg-brand text-white text-ds-control font-medium whitespace-nowrap cursor-pointer"
          >
            Book a Consultation
          </button>
          </div>
        </div>
      )}
    </header>
  );
}